import {
  Component,
  EventEmitter,
  forwardRef,
  Inject,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output
} from '@angular/core';
import {
  BlockUI,
  NgBlockUI
} from 'ng-block-ui';

import {
  Observable,
  Subscription
} from 'rxjs';

import { TranslateService } from '@ngx-translate/core';

import { ExpanderComponent }                             from '../table/cell-components/actions/expander.component';
import { HaynesProLaborOptionModel }                     from './typings/haynes-pro.labor-option';
import { HaynesProLaborTimeModel }                       from './typings/haynes-pro.labor-time';
import { HaynesProMaintenanceTimesExpandedRowComponent } from './haynes-pro.maintenance-times.row.expanded.component';
import { HaynesProService }                              from './haynes-pro.service';
import { HaynesProServiceIntervalModel }                 from './typings/haynes-pro.service-interval';
import { Table }                                         from '../table/types/table';
import { VehicleModel }                                  from '../vehicles/models/vehicle';
import { PushPopComponent }                              from '../table/cell-components/actions/push-pop.component';

@Component({
  selector   : 'wor-haynes-pro-maintenance-times',
  template: require('./haynes-pro.maintenance-times.component.html')
})
export class HaynesProMaintenanceTimesComponent implements OnDestroy, OnInit, OnChanges {
  private laborTimeRemovalSubscription: Subscription;
  public tableSettings                : Table;

  private calculate                                            = new Set<number>();
  public laborOptions       : Array<HaynesProLaborOptionModel> = [];
  public laborTimesIncluded : Array<HaynesProLaborTimeModel>   = [];
  public laborTimes         : Array<HaynesProLaborTimeModel>   = [];
  public selectedSystemId   : number | null                    = null;

  @BlockUI('haynesProMaintenanceTimesBlock') block : NgBlockUI;

  @Input() intervals       : Array<HaynesProServiceIntervalModel>;
  @Input() removedLaborTime: Observable<HaynesProLaborTimeModel>;
  @Input() vehicle         : VehicleModel;
  @Input() viewMode        : boolean;

  @Output() onTimeAdd      : EventEmitter<HaynesProLaborTimeModel> = new EventEmitter();
  @Output() onTimeRemove   : EventEmitter<HaynesProLaborTimeModel> = new EventEmitter();

  constructor (
    @Inject(forwardRef(() => HaynesProService))
    private haynesProService: HaynesProService,
    public translateService : TranslateService
  ) {}

  ngOnChanges () : void {
    if (this.intervals?.length && this.selectedSystemId == null) {
      this.selectedSystemId = this.intervals[0].system_id;
    }
  }

  ngOnDestroy () : void {
    if (this.laborTimeRemovalSubscription) {
      this.laborTimeRemovalSubscription.unsubscribe();
    }
  }

  ngOnInit () : void {
    this.laborTimeRemovalSubscription = this.removedLaborTime.subscribe(( time : HaynesProLaborTimeModel ) => {
      if (time.builtFromServiceInterval) {
        this.haynesProService.updateServiceIntervalsWithRemovedLaborTime(this.intervals, time);
      }
      else {
        this.haynesProService.removeLaborTime(this.laborTimes, time);
      }
    });

    this.tableSettings = {
      columns: this.buildColumns(),
      empty  : {
        text: this.translateService.instant('QUERY_DROPDOWN_SPACE.GRID.NO_RECORDS_FOUND', {
        type: this.translateService.instant('COMPANY_SPACE.INTEGRATIONS.HAYNES_PRO.MAINTENANCE_TIMES')
      })
      },
      rows: {
        expand: {
          enabled     : true,
          factory     : HaynesProMaintenanceTimesExpandedRowComponent,
          isExpandable: true,
          bindings    : {
            inputs: {
              rowExpansionData: this.handleRowExpansion,
              viewMode        : this.viewMode
            },
            outputs: {
              onTimeAdd   : ( interval : any ) => this.onTimeAdd.emit(interval),
              onTimeRemove: ( interval : any ) => this.onTimeRemove.emit(interval)
            }
          }
        },
        highlight: {
          enabled: true
        }
      },
      pagination: {
        enabled: false
      },
      sorting: {
        enabled: false
      }
    };
  }

  buildColumns() {
    const columns:any = [
      {
        field : 'period_name',
        header: {
          text: this.translateService.instant('COMPANY_SPACE.INTEGRATIONS.HAYNES_PRO.PERIOD_NAME')
        }
      },
      {
        field : 'labour_time',
        header: {
          text: this.translateService.instant('GENERAL_SPACE.FIELD.LABOUR_TIME')
        }
      }
    ];

    if (!this.viewMode) {
      columns.push({
        classes  : [ 'text-center' ],
        component: {
          bindings: {
            inputs: {
              popTitle : this.translateService.instant('GENERAL_SPACE.BUTTON.REMOVE_FROM_CART'),
              pushTitle: this.translateService.instant('GENERAL_SPACE.BUTTON.ADD_TO_CART'),
            },
            outputs: {
              onPop : ( interval : HaynesProServiceIntervalModel ) => this.handleIntervalRemoval(interval),
              onPush: ( interval : HaynesProServiceIntervalModel ) => this.handleIntervalSelection(interval)
            }
          },
          enabled: true,
          factory: PushPopComponent,
        },
        sortable: false,
      });
    }

    columns.push({
      component: {
        bindings: {
          inputs: {
            isExpandable: true,
          }
        },
        enabled: true,
        factory: ExpanderComponent,
      },
      sortable: false
    });

    return columns;
  };

  get selectedSchedule () : any {
    if (!this.intervals?.length || !Array.isArray(this.intervals)){
      return null;
    }
    const selectedId = this.selectedSystemId != null ? Number(this.selectedSystemId) : null;

    return this.intervals?.find(s => s.system_id === selectedId) || this.intervals[0];
  }

  handleIntervalSelection ( interval: HaynesProServiceIntervalModel & any) : void {
    this.block.start();

    this.prepareRow(interval)
    .then(( laborOptions : Array<HaynesProLaborOptionModel> ) => {
      this.laborTimes   = [];

      const opts = laborOptions.length ? laborOptions : (interval.___rawLaborOptions || []);

      this.onTimeAdd.emit(this.haynesProService.buildLaborTimeFromInterval(interval, opts));

      this.laborOptions       = interval.__laborOptions || [];
      this.laborTimesIncluded = interval.__laborTimesIncluded || [];
    })
    .finally(() => {
      this.block.stop();
    });
  }

  handleRowExpansion = ( row : any ) : void => {
    if (row.__activated){
      return;
    }

    this.block.start();

    this.prepareRow(row)
      .finally(() => {
        if (this.block.isActive){
          this.block.stop();
        }
      });
  };

  handleIntervalRemoval ( interval : HaynesProServiceIntervalModel ) : void {
    this.onTimeRemove.emit(this.haynesProService.buildLaborTimeFromInterval(interval, this.laborOptions));

    this.laborOptions = [];
    this.laborTimes   = [];
  }

  handleLaborOptionSelection ( option : HaynesProLaborOptionModel ) : void {
    this.block.start();

    this.laborTimes = [];

    setTimeout(() => {
      this.laborTimes = this.haynesProService.removeTimesWithNoLabor(option.tasks);

      this.block.stop();
    }, 500);
  }

  prepareRow ( row: any ) : Promise<Array<HaynesProLaborOptionModel>> {
    const key = row.period_id;

    if (row.__activated) {
      return Promise.resolve(row.___rawLaborOptions || []);
    }

    if (this.calculate.has(key)) {
      return Promise.resolve([]);
    }

    this.calculate.add(key);

    return this.haynesProService.getLaborOptions(this.vehicle, row)
    .then((opts) => {
      row.___rawLaborOptions   = opts;
      row.__laborOptions       = this.haynesProService.removeLaborOptionsWithNoLabor(opts);
      row.__laborTimesIncluded = this.haynesProService.addLaborTimesWithNoLabor(opts);
      row.__activated          = true;

      return opts;
    })
    .finally(() => {
      this.calculate.delete(key);
    });
  }
}
